'use client';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { flatten } from 'flat';

/** Coerce react-hook-form multi-select field values to a string[] so toggling never drops entries (e.g. `other:…`). */
export function normalizeMultiSelectFieldValue(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((v): v is string => typeof v === 'string');
  }
  if (typeof raw === 'string' && raw !== '') {
    return [raw];
  }
  return [];
}

/**
 * useFormHelper is a custom hook that helps with form handling.
 * It uses the useFormContext from react-hook-form to provide form handling capabilities.
 * It provides error handling, value watching and change handlers for form fields.
 * It enforces the field ID is registered on the form if we are using the form
 */
export function useFormHelper(props: {
  id?: string;
  value?: string | number | readonly string[] | undefined;
  error?: string;
  onChange?: React.ChangeEventHandler;
  onValueChange?: (value: string) => void;
  multiple?: boolean;
}) {
  const form = useFormContext();

  if (form) {
    if (!props.id) throw new Error('id is required fields inside "Form".');
    // if (form.getValues(props.id) === undefined)
    //   throw new Error(
    //     `"${props.id}" does not exist on this form. Register it or set a default value.`
    //   );
  }

  function onChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) {
    const { type, value: inputValue } = e.currentTarget;
    const checked = type === 'checkbox' ? (e.currentTarget as HTMLInputElement).checked : undefined;
    const value =
      props.multiple && form
        ? toggleCheckboxValue(
            normalizeMultiSelectFieldValue(form.getValues(props.id || '')),
            inputValue,
            checked || false,
          )
        : inputValue;

    const shouldValidate = !!errorMessage;
    form
      ? form.setValue(props?.id || '', value, { shouldValidate })
      : props.onChange?.(e);
  }

  function toggleCheckboxValue(currentValues: string[], value: string, checked: boolean) {
    if (checked) {
      return currentValues.includes(value) ? currentValues : [...currentValues, value];
    }
    return currentValues.filter((v) => v !== value);
  }

  function onValueChange(value: string) {
    form ? form.setValue(props?.id || '', value) : props.onValueChange?.(value);
  }

  const formValue = form?.watch()[props.id || ''];
  const value = (typeof formValue === 'string' || Array.isArray(formValue)) ? formValue : props.value;
  const errorMessages = flatten(form?.formState.errors || {}) as Record<string, string>

  const errorMessage = props.error
    ? props.error
    : form?.formState.errors
      ? errorMessages[`${props.id}.message`]
      : undefined;

  return { errorMessage, value, onChange, onValueChange };
}
